import FleetModal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import {
	useSelectBankStatementType,
	useSelectMasterBank,
} from '@/libs/fleet-api/hooks/master-data';
import { SubmitMPPBankStatement } from '@/libs/fleet-api/services/mpp';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import { useMutation } from '@tanstack/react-query';
import {
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Upload,
	UploadFile,
} from 'antd';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { FC } from 'react';
import dayjs from 'dayjs';

export const useBankStatementModal = () => {
	return useAsyncModal<
		{
			customerId?: string;
			prospectId: string;
		},
		SubmitMPPBankStatement
	>();
};

const BankStatementModal: FC<ReturnType<typeof useBankStatementModal>> = ({
	modal,
	close,
	data,
}) => {
	const { form, fieldName } = useForm<SubmitMPPBankStatement>();

	const selectMasterBank = useSelectMasterBank();
	const selectBankStatementType = useSelectBankStatementType();

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<FleetModal
			{...modal}
			width={940}
			closeIcon={false}
			keyboard={false}
			maskClosable={false}
			buttonPosition="end"
			okText="SAVE"
			cancelText="CANCEL"
			onCancel={() => close()}
			onOk={() => form.submit()}
			afterOpenChange={(isOpen) => {
				if (!isOpen) form.resetFields();
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<FleetTitle>Add Data Rekening</FleetTitle>
			</div>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={close}
			>
				<Form.Item
					name={fieldName('id')}
					noStyle
				/>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Jenis Rekening Bank"
							name={fieldName(['accountType'])}
							labelCol={{ span: 10 }}
							rules={[rule.required()]}
						>
							<Select {...selectBankStatementType} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName(['bankStatementFile'])}
							label="Upload Dokumen"
							labelCol={{ span: 10 }}
							rules={[rule.required()]}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								maxCount={1}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(fieldName(['bankStatementFile']), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${data!.customerId}/${data!.prospectId}/bank-statements`;

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});

										form.setFieldValue(fieldName(['bankStatementFile']), {
											format: 'jpeg',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName(['bankStatementFile']), null);
									}
								}}
							>
								{uploadMutation.isPending ? (
									<LoadingOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								) : (
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								)}
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Nama Bank"
							name={fieldName(['bankId'])}
							labelCol={{ span: 10 }}
							rules={[rule.required()]}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.name,
										data: value,
									},
								};
							}}
						>
							<Select {...selectMasterBank} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Periode Rekening"
							name={fieldName(['bankStatementPeriod'])}
							labelCol={{ span: 10 }}
							rules={[rule.required()]}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
						>
							<DatePicker.MonthPicker />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Account No"
							name={fieldName(['accountNo'])}
							labelCol={{ span: 10 }}
							rules={[rule.required(), rule.strNumber()]}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</FleetModal>
	);
};

export default BankStatementModal;
